import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

class TokenManager {
  constructor() {
    this.accessToken = localStorage.getItem('accessToken');
    this.refreshToken = localStorage.getItem('refreshToken');
  }

  setTokens(accessToken, refreshToken) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
  }

  clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }

  getAccessToken() {
    return this.accessToken;
  }

  isAuthenticated() {
    return !!this.accessToken;
  }
}

const tokenManager = new TokenManager();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token and email to requests
api.interceptors.request.use((config) => {
  const token = tokenManager.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const email = localStorage.getItem('userEmail');
  if (email) {
    config.headers['x-user-email'] = email;
  }
  return config;
});

// Handle token expiry - redirect to login
const isTokenExpiredError = (error) => {
  const msg = (error.response?.data?.error || '').toLowerCase();
  return msg.includes('token') || msg.includes('expired') || msg.includes('authentication failed') || msg.includes('access denied');
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;

    // Try refresh on 401
    if (status === 401 && tokenManager.refreshToken && !error.config._retry) {
      error.config._retry = true;
      try {
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refreshToken: tokenManager.refreshToken
        });
        tokenManager.setTokens(response.data.accessToken, response.data.refreshToken);
        error.config.headers.Authorization = `Bearer ${response.data.accessToken}`;
        return axios.request(error.config);
      } catch (refreshError) {
        tokenManager.clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Redirect to login on token-related errors
    if (status === 401 || (status === 500 && isTokenExpiredError(error))) {
      tokenManager.clearTokens();
      window.location.href = '/login';
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export const documentService = {
  uploadDocument: async (file, tags) => {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('tags', tags.join(','));
    
    const response = await api.post('/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  searchDocuments: async (searchTerm = '') => {
    const response = await api.get(`/documents/search?q=${encodeURIComponent(searchTerm)}`);
    return response.data;
  },

  getAllDocuments: async () => {
    const response = await api.get('/documents');
    return response.data;
  },

  getDocument: async (id) => {
    const response = await api.get(`/documents/${id}`);
    return response.data;
  },

  getDownloadUrl: async (id) => {
    const response = await api.get(`/documents/${id}/download`);
    return response.data;
  },
};

export const authService = {
  getAuthUrl: async () => {
    const response = await api.get('/auth/login');
    return response.data;
  },

  handleCallback: async (code) => {
    try {
      const response = await api.post('/auth/callback', { code });
      tokenManager.setTokens(response.data.accessToken, response.data.refreshToken);
      if (response.data.email) {
        localStorage.setItem('userEmail', response.data.email);
      }
      if (response.data.role) {
        localStorage.setItem('userRole', response.data.role);
      }
      return response.data;
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.error === 'unauthorized') {
        const email = error.response.data.email || '';
        throw { unauthorized: true, email };
      }
      throw error;
    }
  },

  logout: () => {
    tokenManager.clearTokens();
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userRole');
  },

  isAuthenticated: () => {
    return tokenManager.isAuthenticated();
  },

  isAdmin: () => {
    return localStorage.getItem('userRole') === 'admin';
  }
};

export default api;

export const adminService = {
  getAllowedUsers: async () => {
    const response = await api.get('/admin/users');
    return response.data;
  },

  addUser: async (email) => {
    const response = await api.post('/admin/users', { email });
    return response.data;
  },

  removeUser: async (email) => {
    const response = await api.delete(`/admin/users/${encodeURIComponent(email)}`);
    return response.data;
  }
};